import { useState } from "react";
import * as XLSX from "xlsx";
import { apiFetch } from "../../../context/AuthContext";

// Per-row time estimate used only to drive the progress widget's bar/ETA —
// the backend has no real progress events (the whole import is one request),
// so this is a rough guess based on row count, clamped to a sane range. Same
// constants as the Employees import flow's estimate
// (pages/(admin)employeemanagement/EmployeeManagement.jsx).
const IMPORT_MS_PER_ROW = 120;
const IMPORT_MIN_ESTIMATE_MS = 3000;
const IMPORT_MAX_ESTIMATE_MS = 5 * 60 * 1000;

const useAllowanceDeductionImport = ({ t, showAlert, refreshData }) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  // ── Floating progress card (mirrors the Employees import progress widget) ──
  const [adImportProgress, setAdImportProgress] = useState({
    visible: false,
    minimized: false,
    percent: 0,
    etaSeconds: null,
    fileName: "",
  });

  // ── Results modal (errors + duplicates) shown once the import finishes ──
  const [adImportResultsOpen, setAdImportResultsOpen] = useState(false);
  const [adImportResultsData, setAdImportResultsData] = useState(null);

  const handleOpenAllowanceDeductionImport = () => {
    const input = document.getElementById("allowance-deduction-import-input");
    if (input) {
      input.value = "";
      input.click();
    }
  };

  const handleMinimizeAdImportProgress = () =>
    setAdImportProgress((prev) => ({ ...prev, minimized: true }));
  const handleExpandAdImportProgress = () =>
    setAdImportProgress((prev) => ({ ...prev, minimized: false }));
  const handleCloseAdImportProgress = () =>
    setAdImportProgress((prev) => ({ ...prev, visible: false }));
  const closeAdImportResults = () => {
    setAdImportResultsOpen(false);
    setAdImportResultsData(null);
  };

  const handleAllowanceDeductionFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtension = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!validExtension) {
      showAlert(t("error"), t("allowanceDeductionImportError"), "error");
      return;
    }

    let progressTimer = null;

    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(new Uint8Array(e.target.result));
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });

      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, {
        raw: false,
        defval: "",
      });

      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Empty file");
      }

      // ─────────────────────────────────────────────────────────────────────────
      // Reserved header aliases — these map to the required scalar fields.
      // Everything else is treated as an allowance or deduction column.
      // clientCode is the preferred employee identifier; infinityCode is only
      // a fallback for rows/sheets that don't have a clientCode column.
      // ─────────────────────────────────────────────────────────────────────────
      const CLIENT_CODE_ALIASES = new Set([
        "clientcode",
        "client code",
        "code",
      ]);
      const INFINITY_CODE_ALIASES = new Set([
        "infcode",
        "infinity code",
        "infinitycode",
        "employee code",
      ]);
      const DATE_ALIASES = new Set(["date", "payroll date", "period date"]);

      const records = rows
        .map((row) => {
          const record = {};

          for (const [key, rawValue] of Object.entries(row)) {
            const value =
              rawValue === null || rawValue === undefined ? "" : rawValue;
            const normalizedHeader = String(key)
              .trim()
              .toLowerCase()
              .replace(/\s+/g, " ");

            // ── reserved scalars ──────────────────────────────────────────────
            if (CLIENT_CODE_ALIASES.has(normalizedHeader)) {
              record.clientCode = String(value).trim();
              continue;
            }
            if (INFINITY_CODE_ALIASES.has(normalizedHeader)) {
              record.infinityCode = String(value).trim();
              continue;
            }
            if (DATE_ALIASES.has(normalizedHeader)) {
              record.date = String(value).trim();
              continue;
            }
            // ── skip empty values ─────────────────────────────────────────────
            const strValue = String(value).trim();
            if (strValue === "" || strValue === "0") continue;

            // ── pass every other column through as-is ─────────────────────────
            // The backend's extractFlatImportLines + new _days logic handles
            // classifying columns as allowances, deductions-by-amount, or
            // deductions-by-days.
            record[String(key).trim()] = strValue;
          }

          return record;
        })
        .filter((row) => (row.clientCode || row.infinityCode) && row.date);

      if (records.length === 0) {
        throw new Error(
          "No valid records found — every row must have a client code (or infinity code) and a date",
        );
      }

      // ── Detect duplicate rows within the uploaded file itself ──────────────
      // The import endpoint has no upsert/skip-on-duplicate behavior — two
      // rows for the same employee on the same date both get imported, which
      // usually means the same allowance/deduction gets applied twice. This
      // doesn't block the import (a legitimate case — e.g. two different
      // allowance columns filled in on two separate rows — looks identical to
      // an accidental copy/paste duplicate from here), it just surfaces the
      // groups so the results modal can flag them for review.
      const dupGroups = new Map();
      records.forEach((row) => {
        const employeeCode = String(row.clientCode || row.infinityCode || "").trim();
        if (!employeeCode) return;
        const key = `${employeeCode.toLowerCase()}::${row.date}`;
        const existing = dupGroups.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          dupGroups.set(key, { employeeCode, date: row.date, count: 1 });
        }
      });
      const duplicateRows = Array.from(dupGroups.values()).filter((g) => g.count > 1);

      // ── Floating progress card ──────────────────────────────────────────────
      const estimatedTotalMs = Math.min(
        IMPORT_MAX_ESTIMATE_MS,
        Math.max(IMPORT_MIN_ESTIMATE_MS, records.length * IMPORT_MS_PER_ROW),
      );
      setAdImportProgress({
        visible: true,
        minimized: false,
        percent: 0,
        etaSeconds: estimatedTotalMs / 1000,
        fileName: file.name,
      });
      const startedAt = Date.now();
      progressTimer = setInterval(() => {
        const elapsedMs = Date.now() - startedAt;
        const percent = Math.min(95, (elapsedMs / estimatedTotalMs) * 100);
        const etaSeconds = Math.max(0, (estimatedTotalMs - elapsedMs) / 1000);
        setAdImportProgress((prev) =>
          prev.visible ? { ...prev, percent, etaSeconds } : prev,
        );
      }, 250);

      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/bulk/import-allowances-deductions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ records }),
          timeout: 300000,
        },
      );

      const result = await response.json();

      clearInterval(progressTimer);
      progressTimer = null;
      setAdImportProgress((prev) => ({ ...prev, percent: 100, etaSeconds: 0 }));
      setTimeout(() => {
        setAdImportProgress((prev) => ({ ...prev, visible: false }));
      }, 1200);

      if (!response.ok || !result?.success) {
        throw new Error(result?.error || "Import failed");
      }

      const processedCount = result?.data?.processed || 0;
      const totalCount = result?.data?.total || records.length;
      const failedCount = result?.data?.failed || 0;
      const errors = Array.isArray(result?.data?.errors) ? result.data.errors : [];

      // Always surface the full results modal — it shows successes, failed
      // rows with their reasons, and any duplicate employee/date groups
      // detected above, instead of an alert that only ever showed the first
      // error.
      setAdImportResultsData({
        total: totalCount,
        processed: processedCount,
        failed: failedCount,
        createdAllowances: result?.data?.createdAllowances || 0,
        createdDeductions: result?.data?.createdDeductions || 0,
        errors,
        duplicates: duplicateRows,
        fileName: file.name,
      });
      setAdImportResultsOpen(true);

      if (processedCount > 0) {
        refreshData();
      }
    } catch (error) {
      if (progressTimer) clearInterval(progressTimer);
      setAdImportProgress((prev) => ({ ...prev, visible: false }));
      showAlert(
        t("error"),
        `${t("allowanceDeductionImportError")}: ${error.message}`,
        "error",
      );
    }
  };

  return {
    handleOpenAllowanceDeductionImport,
    handleAllowanceDeductionFileSelect,

    // Progress widget
    adImportProgress,
    handleMinimizeAdImportProgress,
    handleExpandAdImportProgress,
    handleCloseAdImportProgress,

    // Results modal
    adImportResultsOpen,
    adImportResultsData,
    closeAdImportResults,
  };
};

export default useAllowanceDeductionImport;
